/**
 * Zeigt Termin-Erinnerungen (Roadmap F-04 f) - auf jeder Seite, solange die
 * App offen ist. Unten rechts als Karte; liegt der Tab im Hintergrund und
 * ist es erlaubt, zusaetzlich als Benachrichtigung des Browsers.
 *
 * Grenze: Ohne Server gibt es keine Push-Nachricht. Ist die App zu, wird
 * nicht erinnert; beim naechsten Oeffnen erscheinen die Erinnerungen der
 * letzten 15 Minuten noch. Jede Erinnerung erscheint nur einmal (gemerkt
 * je Geraet im Browser-Speicher).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';

import { getOccurrences } from '@/db/repositories/events';
import { erinnerungsText, faelligeErinnerungen, type Erinnerung as Grund } from '@/domain/calendar/reminders';
import { useCalendarStore } from '@/store/calendar';
import { useUiStore } from '@/store/ui';
import { addDays, addMinutes, datePart, formatDe, formatTime, now, today } from '@/lib/datetime';
import type { LocalDateTime } from '@/domain/types';

/** Mit dem Zeitpunkt, zu dem sie erschien - fuer den Text. */
type Erinnerung = Grund & { gezeigt: LocalDateTime };

const SPEICHER = 'ole-privat:erinnerungen';
/** So weit zurueck erscheinen verpasste Erinnerungen beim Oeffnen noch. */
const NACHHOLEN_MIN = 15;
const TAKT_MS = 20_000;

/** Gezeigte Erinnerungen mit Zeitpunkt - aeltere als drei Tage fallen weg. */
function gezeigtLaden(): Map<string, string> {
  try {
    const roh = JSON.parse(localStorage.getItem(SPEICHER) ?? '{}') as Record<string, string>;
    const grenze = addDays(now(), -3);
    return new Map(Object.entries(roh).filter(([, zeit]) => typeof zeit === 'string' && zeit > grenze));
  } catch {
    return new Map();
  }
}

function gezeigtSpeichern(m: Map<string, string>): void {
  try {
    localStorage.setItem(SPEICHER, JSON.stringify(Object.fromEntries(m)));
  } catch {
    // Ohne Speicher erscheint eine Erinnerung nach dem Neuladen evtl. erneut - kein Schaden.
  }
}

function benachrichtigen(e: Erinnerung): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return; // Die Karte reicht.
  try {
    const zeit = e.occurrence.allDay ? 'ganztägig' : formatTime(String(e.occurrence.start) as LocalDateTime);
    new Notification(e.occurrence.title || 'Termin', { body: `${erinnerungsText(e, now())} · ${zeit}`, tag: e.key });
  } catch {
    // Manche Browser erlauben Benachrichtigungen nur ueber einen Service Worker.
  }
}

export function ErinnerungsHost() {
  const [offen, setOffen] = useState<Erinnerung[]>([]);
  const navigate = useNavigate();
  const setView = useUiStore((s) => s.setCalendarView);
  const setAnchor = useCalendarStore((s) => s.setAnchor);
  const select = useCalendarStore((s) => s.select);

  useEffect(() => {
    let aktiv = true;
    let seit = addMinutes(now(), -NACHHOLEN_MIN);

    async function pruefen() {
      const jetzt = now();
      const heute = today();
      // "1 Tag vorher" braucht Termine bis uebermorgen.
      const vorkommen = await getOccurrences({
        from: `${addDays(heute, -1)}T00:00` as LocalDateTime,
        to: `${addDays(heute, 3)}T00:00` as LocalDateTime,
      });
      if (!aktiv) return;
      const gezeigt = gezeigtLaden();
      const neu: Erinnerung[] = faelligeErinnerungen(vorkommen, seit, jetzt)
        .filter((e) => !gezeigt.has(e.key)).map((e) => ({ ...e, gezeigt: jetzt }));
      seit = jetzt;
      if (neu.length === 0) return;
      for (const e of neu) gezeigt.set(e.key, jetzt);
      gezeigtSpeichern(gezeigt);
      neu.forEach(benachrichtigen);
      setOffen((alt) => [...alt, ...neu]);
    }

    void pruefen();
    const id = window.setInterval(() => void pruefen(), TAKT_MS);
    return () => { aktiv = false; window.clearInterval(id); };
  }, []);

  if (offen.length === 0) return null;

  const weg = (key: string) => setOffen((alt) => alt.filter((e) => e.key !== key));

  function oeffnen(e: Erinnerung) {
    setView('tag');
    setAnchor(datePart(String(e.occurrence.start)));
    select(e.occurrence);
    weg(e.key);
    navigate('/kalender');
  }

  return (
    // Der Platz unten rechts kommt aus der AppShell (gemeinsam mit der Rueckgaengig-Meldung, F-45).
    <section aria-label="Erinnerungen" aria-live="polite" className="flex w-full flex-col gap-2">
      {offen.slice(-4).map((e) => (
        <div key={e.key} role="status" className="pointer-events-auto flex items-start gap-2.5 rounded-lg border border-line-strong bg-surface-raised px-3.5 py-3 shadow-lg">
          <Bell className="mt-0.5 size-4 shrink-0 text-accent-text" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{e.occurrence.title || 'Ohne Titel'}</p>
            <p className="text-[12px] text-ink-muted">
              {erinnerungsText(e, e.gezeigt)}
              {!e.occurrence.allDay && ` · ${formatDe(String(e.occurrence.start), 'EEEEEE HH:mm')}`}
              {e.occurrence.location && ` · ${e.occurrence.location}`}
            </p>
            <button type="button" onClick={() => oeffnen(e)} className="mt-1 text-[12px] text-accent-text hover:underline">
              Im Kalender öffnen
            </button>
          </div>
          <button type="button" onClick={() => weg(e.key)} aria-label={`Erinnerung an ${e.occurrence.title} schließen`}
            className="grid size-6 shrink-0 place-items-center rounded text-ink-faint hover:text-ink">
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      {offen.length > 1 && (
        <button type="button" onClick={() => setOffen([])} className="pointer-events-auto self-end text-[12px] text-ink-faint hover:text-ink">
          Alle schließen ({offen.length})
        </button>
      )}
    </section>
  );
}
